import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ConfigProvider, Modal, Popconfirm } from 'antd';
import type { ThemeConfig } from 'antd';
import Button from 'components/common/Button';
import Notice from 'components/common/Notice/Notice';
import {
  COPY_TOOL_AREA_LOAD_ERROR_MESSAGE,
  COPY_TOOL_AREA_SUMMARY_MESSAGE,
  COPY_TOOL_AREA_UNKNOWN_MESSAGE,
  COPY_TOOL_COPY_DOCUMENT_LABEL,
  COPY_TOOL_COPY_ERROR_MESSAGE,
  COPY_TOOL_COPY_NEW_DOCUMENT_LABEL,
  COPY_TOOL_COPY_RUNNING_REASON,
  COPY_TOOL_CROP_ONLY_SUMMARY_MESSAGE,
  COPY_TOOL_NO_DESTINATION_MESSAGE,
  COPY_TOOL_NO_SOURCE_MESSAGE,
  COPY_TOOL_NO_TASKS_CROP_ONLY_MESSAGE,
  COPY_TOOL_NO_TASKS_MESSAGE,
  COPY_TOOL_NOTHING_SELECTED_MESSAGE,
  COPY_TOOL_REPLACE_CONFIRM_DESCRIPTION,
  COPY_TOOL_REPLACE_CONFIRM_TITLE,
  COPY_TOOL_SELECTION_SUMMARY_MESSAGE,
  COPY_TOOL_SWITCHED_TO_ADD_TO_NOTE,
  COPY_TOOL_UNKNOWN_PADDOCK_NAME,
  COPY_TOOL_UNSAVED_CHANGES_WARNING,
} from 'constants/messageConstants';
import {
  AREA_UNIT,
  COPY_TOOL_CANCEL_LABEL,
  COPY_TOOL_COPY_LABEL,
  COPY_TOOL_COPYING_LABEL,
  COPY_TOOL_DESTINATION_HINT,
  COPY_TOOL_DESTINATION_LABEL,
  COPY_TOOL_PROCEED_LABEL,
  COPY_TOOL_SOURCE_HINT,
  COPY_TOOL_SOURCE_LABEL,
  COPY_TOOL_SWAP_DISABLED_ADD_AS_NEW,
  COPY_TOOL_TITLE,
  PLAN_ACTUAL_ACTUAL_LABEL,
  PLAN_ACTUAL_PLAN_LABEL,
} from 'constants/uiConstants';
import { useSelectedInput } from 'hooks/inputs/useSelectedInput';
import { useFetchZonesByOrgId } from 'hooks/useFetchZonesByOrgId';
import { useVisibleOrgData } from 'hooks/useVisibleOrgData';
import {
  CopyDataCategory,
  CopyMatchRule,
  CopyMode,
  DEFAULT_COPY_OPTIONS,
  ICopyOptions,
  ICopyTarget,
} from 'interfaces/ICopyPaddockData';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { remToPx } from 'utils/cssUnits';
import {
  buildPaddockNameMap,
  getInputDisplayName,
  getInputYear,
  indexInputsByPaddock,
} from 'utils/FarmUtils';
import { toast } from 'utils/toast';
import { toHectaresOrNull } from 'utils/unitConversion';

import { getActiveOrganisationId } from '../../redux/selectors/ActiveOrganisationSelectors';
import { getIsCopyRunning } from '../../redux/selectors/CopyToolSelectors';
import { getTreeYearFilter } from '../../redux/selectors/FarmSelectors';
import {
  getActiveTaskCategory,
  getHasUnsavedInputChanges,
} from '../../redux/selectors/InputSelectors';
import { closeCopyTool } from '../../redux/slices/CopyToolSlice';

import { getExistingTargetId } from './buildCopiedInput';
import {
  buildCopyTreeData,
  excludeInputFromCopyTree,
} from './buildCopyTreeData';
import {
  applyCategoryChange,
  applyMatchRuleChange,
  applyTaskCheck,
  CopyDataFilterContext,
  CopyDataFilterState,
  countTasksByCopyCategory,
  createFullCopyFilter,
  getCopyFilterSourceKey,
  getCopySourceTasks,
  getLockedTaskIds,
  hasCopyCropAndVariety,
  isFullCopyFilter,
  toCopyDataFilter,
  withLockedTaskTicks,
} from './copyDataFilter';
import CopyDataFilterControls from './CopyDataFilterControls';
import {
  buildCopyRequest,
  buildCopySummary,
  convertDestinationKeys,
  CopySideState,
  getCopySourceInput,
  getOpenInputKeys,
  normaliseCopyKeys,
  restoreDocumentKeys,
  SavedDocumentKeys,
  swapCopySides,
} from './copySelection';
import CopySelectionTree from './CopySelectionTree';
import CopyTaskChecklist from './CopyTaskChecklist';
import {
  CopyModeControl,
  CopyOptionsControl,
  CopySideControls,
} from './CopyToolControls';
import { getPaddockExpandedKeys, indexPaddocksById } from './copyTreeSearch';
import SwapButton from './SwapButton';
import { useCopyPaddockData } from './useCopyPaddockData';
import { useCopyTreeView } from './useCopyTreeView';

// Source takes one document for now; one-to-many and many-to-one may return (OP-2489).
const SOURCE_ALLOW_MULTIPLE = false;
const DESTINATION_ALLOW_MULTIPLE = true;

const MODE_LABELS: Record<InputTaskCategory, string> = {
  [InputTaskCategory.Plan]: PLAN_ACTUAL_PLAN_LABEL,
  [InputTaskCategory.Actual]: PLAN_ACTUAL_ACTUAL_LABEL,
};

const NO_ZONE_AREAS = new Map<string, number | null>();

/**
 * The modal's body text is --font-sm, set per component so antd's smaller
 * sizes stay as they are. antd's theme takes pixels, so the rem token is read
 * at runtime; without it (e.g. tests) antd's defaults stay.
 */
const getCopyToolTheme = (): ThemeConfig => {
  const fontSize = remToPx(
    parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--font-sm')
    )
  );
  if (!Number.isFinite(fontSize)) return {};
  const text = { fontSize };
  return {
    components: {
      Modal: text,
      Checkbox: text,
      Radio: text,
      Tree: text,
      DatePicker: text,
      Segmented: text,
      Tooltip: text,
      Input: text,
      Popconfirm: text,
    },
  };
};

const areaFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

/**
 * Copy data between paddocks (OP-2489 / OP-2519). Mounted by Layout only while
 * open. Source takes one document and Destination many; the swap exchanges
 * the panels' settings and ticks. Copy runs the request through `useCopyPaddockData`.
 */
const CopyToolModal: React.FC = () => {
  const dispatch = useDispatch();
  const activeTaskCategory = useSelector(getActiveTaskCategory);
  const activeOrganisationId = useSelector(getActiveOrganisationId);
  const treeYearFilter = useSelector(getTreeYearFilter);
  const selectedInput = useSelectedInput();
  const hasUnsavedInputChanges = useSelector(getHasUnsavedInputChanges);
  // Pinned on open, so a copy still running after an org switch keeps its own data.
  const [organisationId] = useState(activeOrganisationId);
  // The open plan, if any, is the default source whichever button opened the tool.
  const [openInput] = useState(selectedInput);
  // False once unmounted, so a copy that lands later can't close a reopened tool.
  const isMountedRef = useRef(true);

  const [source, setSource] = useState<CopySideState>(() => ({
    year:
      (openInput ? getInputYear(openInput) : null) ??
      (typeof treeYearFilter === 'number'
        ? treeYearFilter
        : new Date().getFullYear()),
    mode: activeTaskCategory,
    keys: [],
    expandedKeys: null,
  }));
  const [destination, setDestination] = useState<CopySideState>(() => ({
    ...source,
  }));
  // False until the user ticks or swaps, so the open plan is ticked once the trees load.
  const [hasEditedSource, setHasEditedSource] = useState(false);
  const [copyMode, setCopyMode] = useState(CopyMode.Replace);
  const [copyOptions, setCopyOptions] =
    useState<ICopyOptions>(DEFAULT_COPY_OPTIONS);
  // Held while the open plan's own copy lands, so edits made meanwhile can't be lost.
  const [isCopying, setIsCopying] = useState(false);
  // One copy at a time, so this one can't read what a running copy is still writing.
  const isCopyRunning = useSelector(getIsCopyRunning);
  const theme = useMemo(getCopyToolTheme, []);
  // Add as new creates documents, so Destination ticks paddocks rather than documents.
  const destinationPaddocksOnly = copyMode === CopyMode.AddAsNew;
  // Destination's ticks from before Add as new, brought back when leaving it.
  const [savedDestination, setSavedDestination] =
    useState<SavedDocumentKeys | null>(null);
  // The data filter's choices; only kept while they belong to the ticked Source and mode.
  const [filterState, setFilterState] = useState<CopyDataFilterState | null>(
    null
  );
  // The rule picked for a full copy, which keeps no ticks of its own.
  const [fullCopyMatchRule, setFullCopyMatchRule] = useState<{
    sourceKey: string;
    matchRule: CopyMatchRule;
  } | null>(null);
  // Set when a partial copy moved Replace to Add to, so the mode doesn't change unexplained.
  const [hasSwitchedToAddTo, setHasSwitchedToAddTo] = useState(false);
  // Blocks a second Copy before the re-render disables the button.
  const isCopyingRef = useRef(false);

  const { farms, inputs, farmsQuery, inputsQuery } =
    useVisibleOrgData(organisationId);
  const isLoading = farmsQuery.isLoading || inputsQuery.isLoading;
  const isError = farmsQuery.isError || inputsQuery.isError;

  const zonesQuery = useFetchZonesByOrgId(organisationId);
  const zonesById = useMemo(
    () => new Map((zonesQuery.data ?? []).map((zone) => [zone.id, zone])),
    [zonesQuery.data]
  );
  const startCopy = useCopyPaddockData(organisationId);
  // A new plan takes its paddock's Zone area, so new-document areas come from the zones.
  const zoneAreaHaById = useMemo(
    () =>
      new Map(
        (zonesQuery.data ?? []).map((zone) => [
          zone.id,
          toHectaresOrNull(zone.zoneArea, zone.areaUnit),
        ])
      ),
    [zonesQuery.data]
  );
  // isLoading, not isPending: a disabled query stays pending and would hide the area for good.
  const isAreaLoading = zonesQuery.isLoading;

  const inputsByPaddock = useMemo(() => indexInputsByPaddock(inputs), [inputs]);
  const inputsById = useMemo(
    () => new Map(inputs.map((input) => [input.id, input])),
    [inputs]
  );
  const sourceTree = useMemo(
    () =>
      buildCopyTreeData({
        farms,
        inputsByPaddock,
        year: source.year,
        allowMultiple: SOURCE_ALLOW_MULTIPLE,
        // Areas are only summed for Destination.
        zoneAreaHaById: NO_ZONE_AREAS,
      }),
    [farms, inputsByPaddock, source.year]
  );
  const sourceDefaultExpandedKeys = useMemo(
    () => getPaddockExpandedKeys(sourceTree.treeData),
    [sourceTree.treeData]
  );
  // Each side as shown: ticks fitted to the year's tree, expansion defaulted.
  const resolvedSource = useMemo<CopySideState>(() => {
    const keys = hasEditedSource
      ? normaliseCopyKeys(source.keys, sourceTree.index, SOURCE_ALLOW_MULTIPLE)
      : getOpenInputKeys(openInput, sourceTree.index);
    return {
      ...source,
      keys,
      expandedKeys: source.expandedKeys ?? sourceDefaultExpandedKeys,
    };
  }, [
    source,
    hasEditedSource,
    openInput,
    sourceTree,
    sourceDefaultExpandedKeys,
  ]);

  const sourceInput = getCopySourceInput(resolvedSource.keys, sourceTree.index);
  const sourceInputId = sourceInput?.id ?? null;
  const filterContext = useMemo<CopyDataFilterContext | null>(() => {
    if (!sourceInput) return null;
    const tasks = getCopySourceTasks(sourceInput, source.mode);
    return {
      tasks,
      hasCropAndVariety: hasCopyCropAndVariety(sourceInput),
      categoryCounts: countTasksByCopyCategory(tasks),
      lockedTaskIds: getLockedTaskIds(tasks),
    };
  }, [sourceInput, source.mode]);
  const filterSourceKey = sourceInput
    ? getCopyFilterSourceKey(sourceInput.id, source.mode)
    : null;
  // A new Source or mode starts from a full copy; a full copy keeps the rule last picked for its Source.
  const activeFilter = useMemo(() => {
    if (!filterContext || !filterSourceKey) return null;
    if (filterState?.sourceKey === filterSourceKey) {
      return withLockedTaskTicks(filterState, filterContext);
    }
    return createFullCopyFilter(
      filterSourceKey,
      filterContext,
      fullCopyMatchRule?.sourceKey === filterSourceKey
        ? fullCopyMatchRule.matchRule
        : undefined
    );
  }, [filterContext, filterSourceKey, filterState, fullCopyMatchRule]);
  const dataFilter = useMemo(
    () =>
      activeFilter && filterContext
        ? toCopyDataFilter(activeFilter, filterContext)
        : null,
    [activeFilter, filterContext]
  );
  const isFullCopy = dataFilter?.isFullCopy ?? true;
  // Crop & variety alone still updates each destination's crop, also from a Source with no tasks; Replace with no tasks would clear them.
  const copiesCropOnly =
    !!dataFilter &&
    dataFilter.includeCropAndVariety &&
    copyMode !== CopyMode.Replace;
  // Excluded only Plan → Plan or Actual → Actual; its Destination ticks are hidden meanwhile.
  // Not in Add as new, which ticks paddocks: a new document in the Source's paddock isn't a self-copy.
  const getExcludedInputId = (paddocksOnly: boolean) =>
    sourceInput && source.mode === destination.mode && !paddocksOnly
      ? sourceInput.id
      : null;
  const excludedInputId = getExcludedInputId(destinationPaddocksOnly);
  // Built per year and Add as new; the Source exclusion is a light pass over it.
  const destinationBaseTree = useMemo(
    () =>
      buildCopyTreeData({
        farms,
        inputsByPaddock,
        year: destination.year,
        allowMultiple: DESTINATION_ALLOW_MULTIPLE,
        paddocksOnly: destinationPaddocksOnly,
        zoneAreaHaById,
      }),
    [
      farms,
      inputsByPaddock,
      destination.year,
      destinationPaddocksOnly,
      zoneAreaHaById,
    ]
  );
  // The exclusion pass doesn't change which paddocks have plans, so the base tree decides.
  const destinationDefaultExpandedKeys = useMemo(
    () => getPaddockExpandedKeys(destinationBaseTree.treeData),
    [destinationBaseTree.treeData]
  );
  const destinationTree = useMemo(
    () => excludeInputFromCopyTree(destinationBaseTree, excludedInputId),
    [destinationBaseTree, excludedInputId]
  );

  const resolvedDestination = useMemo<CopySideState>(() => {
    const keys = normaliseCopyKeys(
      destination.keys,
      destinationTree.index,
      DESTINATION_ALLOW_MULTIPLE,
      destinationPaddocksOnly
    );
    return {
      ...destination,
      keys,
      expandedKeys: destination.expandedKeys ?? destinationDefaultExpandedKeys,
    };
  }, [
    destination,
    destinationTree,
    destinationPaddocksOnly,
    destinationDefaultExpandedKeys,
  ]);

  const request = useMemo(
    () =>
      buildCopyRequest({
        orgId: organisationId ?? '',
        copyMode,
        source: resolvedSource,
        destination: resolvedDestination,
        sourceIndex: sourceTree.index,
        destinationIndex: destinationTree.index,
        filter: dataFilter,
        options: copyOptions,
      }),
    [
      organisationId,
      copyMode,
      copyOptions,
      resolvedSource,
      resolvedDestination,
      sourceTree.index,
      destinationTree.index,
      dataFilter,
    ]
  );
  const summary = useMemo(
    () =>
      request && sourceInput
        ? buildCopySummary(request, sourceInput, destinationTree.index)
        : null,
    [request, sourceInput, destinationTree.index]
  );
  // Replace copies the whole plan, so it never copies a partial filter, however that comes about.
  const isReplaceBlocked = copyMode === CopyMode.Replace && !isFullCopy;
  const canCopy =
    !!summary &&
    (summary.taskCount > 0 || copiesCropOnly) &&
    !isReplaceBlocked &&
    !isCopying &&
    !isCopyRunning;
  // Existing documents a Replace overwrites; new documents for empty paddocks overwrite nothing.
  const replacedDocumentCount =
    copyMode === CopyMode.Replace && request
      ? request.destination.targets.filter((target) => target.inputId !== null)
          .length
      : 0;
  // One warning per copy, before anything is overwritten.
  const needsReplaceConfirm = canCopy && replacedDocumentCount > 0;

  // Replace copies the whole plan, so a partial copy moves to Add to, with a note saying why.
  const switchReplaceToAddTo = () => {
    setCopyMode(CopyMode.AddTo);
    setHasSwitchedToAddTo(true);
  };

  // Owns the note: a full copy clears it. Also catches a partial filter that a Source refetch brings under Replace.
  useEffect(() => {
    if (isReplaceBlocked) switchReplaceToAddTo();
    else if (isFullCopy) setHasSwitchedToAddTo(false);
  }, [isReplaceBlocked, isFullCopy]);

  // Expansion stays on its (per-year) defaults until the user expands or ticks.
  const updateSource = (changes: Partial<CopySideState>) => {
    setSource({
      ...resolvedSource,
      expandedKeys: source.expandedKeys,
      ...changes,
    });
    if (changes.keys) setHasEditedSource(true);
    if (changes.keys || changes.mode) setFilterState(null);
  };
  const updateDestination = (changes: Partial<CopySideState>) => {
    setDestination({
      ...resolvedDestination,
      expandedKeys: destination.expandedKeys,
      ...changes,
    });
  };

  // Each panel keeps its own view and search, which the swap leaves in place.
  const paddocksById = useMemo(() => indexPaddocksById(farms), [farms]);
  const paddockNames = useMemo(() => buildPaddockNameMap(farms), [farms]);
  const sourceView = useCopyTreeView({
    treeData: sourceTree.treeData,
    lookupIndex: sourceTree.index,
    index: sourceTree.index,
    paddocksById,
    paddockNames,
    allowMultiple: SOURCE_ALLOW_MULTIPLE,
    // A Source is a document, so Source has no paddocks-without-plans group.
    includeEmptyPaddocks: false,
    paddocksOnly: false,
    expandedKeys: resolvedSource.expandedKeys ?? [],
    onExpandedKeysChange: (expandedKeys) => updateSource({ expandedKeys }),
  });
  const destinationView = useCopyTreeView({
    treeData: destinationTree.treeData,
    // The base index still holds the Source document, so it can match a search.
    lookupIndex: destinationBaseTree.index,
    index: destinationTree.index,
    paddocksById,
    paddockNames,
    allowMultiple: DESTINATION_ALLOW_MULTIPLE,
    includeEmptyPaddocks: true,
    paddocksOnly: destinationPaddocksOnly,
    expandedKeys: resolvedDestination.expandedKeys ?? [],
    onExpandedKeysChange: (expandedKeys) => updateDestination({ expandedKeys }),
  });

  const handleSwap = () => {
    const swapped = swapCopySides({
      source: resolvedSource,
      destination: resolvedDestination,
      sourceIndex: sourceTree.index,
      destinationIndex: destinationTree.index,
      sourceAllowMultiple: SOURCE_ALLOW_MULTIPLE,
      destinationAllowMultiple: DESTINATION_ALLOW_MULTIPLE,
    });
    setSource(swapped.source);
    setDestination(swapped.destination);
    setHasEditedSource(true);
    setFilterState(null);
  };

  // A full copy's ticks are dropped, keeping its rule, so it follows the Source's tasks if they refetch.
  const resetToFullCopy = (filter: CopyDataFilterState) => {
    setFilterState(null);
    setFullCopyMatchRule({
      sourceKey: filter.sourceKey,
      matchRule: filter.matchRule,
    });
  };

  // A filter change moves Replace to Add to at once, so Replace never shows with a partial copy.
  const saveFilter = (
    next: CopyDataFilterState,
    context: CopyDataFilterContext
  ) => {
    if (isFullCopyFilter(next, context)) {
      resetToFullCopy(next);
      return;
    }
    setFilterState(next);
    if (copyMode === CopyMode.Replace) switchReplaceToAddTo();
  };

  const handleCategoriesChange = (
    categories: CopyDataCategory[],
    includeCropAndVariety: boolean
  ) => {
    if (!activeFilter || !filterContext) return;
    saveFilter(
      applyCategoryChange(
        activeFilter,
        categories,
        includeCropAndVariety,
        filterContext
      ),
      filterContext
    );
  };

  const handleMatchRuleChange = (matchRule: CopyMatchRule) => {
    if (!activeFilter || !filterContext) return;
    saveFilter(
      applyMatchRuleChange(activeFilter, matchRule, filterContext),
      filterContext
    );
  };

  const handleTaskCheck = (taskId: string, checked: boolean) => {
    if (!activeFilter || !filterContext) return;
    saveFilter(
      applyTaskCheck(activeFilter, taskId, checked, filterContext),
      filterContext
    );
  };

  const handleCopyModeChange = (mode: CopyMode) => {
    const toPaddocksOnly = mode === CopyMode.AddAsNew;
    if (toPaddocksOnly && !destinationPaddocksOnly) {
      const paddockKeys = convertDestinationKeys(
        resolvedDestination.keys,
        destinationTree.index,
        true
      );
      setSavedDestination({ keys: resolvedDestination.keys, paddockKeys });
      updateDestination({ keys: paddockKeys });
    } else if (!toPaddocksOnly && destinationPaddocksOnly) {
      // Add as new's tree keeps the Source, so paddocks expand to documents without it.
      const leavingExcludedId = getExcludedInputId(false);
      const documentIndex = leavingExcludedId
        ? excludeInputFromCopyTree(destinationTree, leavingExcludedId).index
        : destinationTree.index;
      updateDestination({
        keys: savedDestination
          ? restoreDocumentKeys(
              resolvedDestination.keys,
              savedDestination,
              documentIndex
            )
          : convertDestinationKeys(
              resolvedDestination.keys,
              documentIndex,
              false
            ),
      });
      setSavedDestination(null);
    }
    // Replace copies the whole plan, so choosing it ticks everything again; with no Source shown, the saved ticks stay.
    if (mode === CopyMode.Replace && activeFilter)
      resetToFullCopy(activeFilter);
    setCopyMode(mode);
    setHasSwitchedToAddTo(false);
  };

  // Closes on unmount (navigation, sign-out); would misfire under StrictMode, which is off.
  useEffect(
    () => () => {
      isMountedRef.current = false;
      dispatch(closeCopyTool());
    },
    [dispatch]
  );

  // Waits while the open plan's copy lands; a copy already started runs on with its own org's data.
  useEffect(() => {
    if (activeOrganisationId !== organisationId && !isCopying) {
      dispatch(closeCopyTool());
    }
  }, [activeOrganisationId, organisationId, isCopying, dispatch]);

  // The open plan's pending edits are saved before the copy; this says so up front.
  const showUnsavedWarning =
    hasUnsavedInputChanges &&
    !!openInput &&
    (sourceInputId === openInput.id ||
      !!request?.destination.targets.some(
        (target) => target.inputId === openInput.id
      ));

  const handleClose = () => {
    if (!isCopying) dispatch(closeCopyTool());
  };

  // Names each destination as the tree did before the copy, so the toast says where it went.
  const labelCopyTarget = (target: ICopyTarget): string => {
    const paddockName =
      paddockNames.get(target.paddockId) ?? COPY_TOOL_UNKNOWN_PADDOCK_NAME;
    const existingId = getExistingTargetId(target, copyMode);
    const input = existingId !== null ? inputsById.get(existingId) : undefined;
    if (input) {
      return COPY_TOOL_COPY_DOCUMENT_LABEL(
        getInputDisplayName(input),
        paddockName
      );
    }
    // An existing document missing from the loaded list is still not new.
    return existingId !== null
      ? paddockName
      : COPY_TOOL_COPY_NEW_DOCUMENT_LABEL(paddockName);
  };

  // The copy runs on in the background, with its own toast; the tool closes once the open plan is safe to edit.
  const handleCopy = async () => {
    if (!request || !sourceInput || !canCopy || isCopyingRef.current) return;
    isCopyingRef.current = true;
    setIsCopying(true);
    let canClose = false;
    try {
      canClose = await startCopy(
        request,
        { source: sourceInput, paddocksById, zonesById },
        labelCopyTarget
      );
    } catch {
      toast.error(COPY_TOOL_COPY_ERROR_MESSAGE);
    }
    if (!isMountedRef.current) return;
    if (canClose) {
      dispatch(closeCopyTool());
      return;
    }
    // Nothing was copied (the open plan couldn't be saved, or it failed to start): stay open to copy again.
    isCopyingRef.current = false;
    setIsCopying(false);
  };

  const renderStatus = () => {
    if (isCopyRunning && !isCopying) return COPY_TOOL_COPY_RUNNING_REASON;
    if (!sourceInput) return COPY_TOOL_NO_SOURCE_MESSAGE;
    if (!summary) return COPY_TOOL_NO_DESTINATION_MESSAGE;
    if (summary.taskCount === 0 && !copiesCropOnly) {
      if (!isFullCopy) return COPY_TOOL_NOTHING_SELECTED_MESSAGE;
      // Only Replace blocks a crop-only copy, so name the modes that copy it.
      return dataFilter?.includeCropAndVariety
        ? COPY_TOOL_NO_TASKS_CROP_ONLY_MESSAGE(MODE_LABELS[resolvedSource.mode])
        : COPY_TOOL_NO_TASKS_MESSAGE(MODE_LABELS[resolvedSource.mode]);
    }
    return (
      <>
        <span className="font-semibold text-charcoal">
          {summary.taskCount > 0
            ? COPY_TOOL_SELECTION_SUMMARY_MESSAGE(
                summary.taskCount,
                summary.stepCount,
                summary.documentCount
              )
            : COPY_TOOL_CROP_ONLY_SUMMARY_MESSAGE(summary.documentCount)}
        </span>
        {/* No area while zones load; no total when none is known, so 0 ha never reads as measured. */}
        {!isAreaLoading && summary.unknownAreaCount < summary.documentCount && (
          <span>
            {COPY_TOOL_AREA_SUMMARY_MESSAGE(
              areaFormat.format(summary.areaHa),
              AREA_UNIT
            )}
          </span>
        )}
        {!isAreaLoading && summary.unknownAreaCount > 0 && (
          <span>
            {/* A failed zones load leaves new documents' areas unknown; say so rather than blame the data. */}
            {zonesQuery.isError
              ? COPY_TOOL_AREA_LOAD_ERROR_MESSAGE
              : COPY_TOOL_AREA_UNKNOWN_MESSAGE(summary.unknownAreaCount)}
          </span>
        )}
      </>
    );
  };

  const footer = (
    <div className="flex flex-wrap items-center justify-between gap-3 pr-2">
      <p
        role="status"
        className="flex flex-wrap gap-x-3 gap-y-1 text-left text-sm text-gray-dark"
      >
        {renderStatus()}
      </p>
      <div className="flex gap-3">
        <Button
          variant="secondary"
          className="px-4 py-1 text-sm font-semibold"
          onClick={handleClose}
          disabled={isCopying}
          type="button"
        >
          {COPY_TOOL_CANCEL_LABEL}
        </Button>
        <Popconfirm
          disabled={!needsReplaceConfirm}
          title={COPY_TOOL_REPLACE_CONFIRM_TITLE}
          description={COPY_TOOL_REPLACE_CONFIRM_DESCRIPTION(
            replacedDocumentCount,
            MODE_LABELS[resolvedDestination.mode]
          )}
          okText={COPY_TOOL_PROCEED_LABEL}
          cancelText={COPY_TOOL_CANCEL_LABEL}
          placement="topRight"
          onConfirm={handleCopy}
        >
          {/* The shared Button doesn't forward a ref, which the confirm needs. */}
          <span className="inline-flex">
            <Button
              variant="success"
              className="px-4 py-1 text-sm font-semibold disabled:opacity-50"
              onClick={needsReplaceConfirm ? undefined : handleCopy}
              disabled={!canCopy}
              type="button"
            >
              {isCopying ? COPY_TOOL_COPYING_LABEL : COPY_TOOL_COPY_LABEL}
            </Button>
          </span>
        </Popconfirm>
      </div>
    </div>
  );

  return (
    <ConfigProvider theme={theme}>
      <Modal
        title={
          <div className="ml-1 text-lg text-center font-semibold text-gray-dark">
            {COPY_TOOL_TITLE}
          </div>
        }
        centered
        open
        onCancel={handleClose}
        footer={footer}
        mask={{ closable: !isCopying }}
        keyboard={!isCopying}
        closable={!isCopying}
        width="65rem"
        className="!max-w-[calc(100vw-2rem)]"
        classNames={{
          root: '!rounded-2xl',
          header: '!mb-0',
          // Fixed height from md up: the trees fill what's left, so the body only scrolls on very short screens.
          body: '!max-h-[85vh] !overflow-y-auto !overflow-x-hidden !p-1 !pr-2 md:!h-[75vh]',
        }}
        destroyOnHidden
      >
        <div className="flex flex-col gap-3 pt-3 md:h-full">
          {showUnsavedWarning && (
            <Notice>{COPY_TOOL_UNSAVED_CHANGES_WARNING}</Notice>
          )}
          <div className="flex flex-col gap-x-3 gap-y-3 lg:flex-row lg:items-start">
            <div className="flex flex-col items-start gap-y-3 lg:w-[calc(50%-0.375rem)] lg:flex-none">
              <div className="flex w-full flex-row flex-wrap gap-4 rounded-md border border-gray-medium p-2">
                <CopySideControls
                  side={COPY_TOOL_SOURCE_LABEL}
                  year={resolvedSource.year}
                  onYearChange={(year) =>
                    updateSource({ year, expandedKeys: null })
                  }
                  mode={resolvedSource.mode}
                  onModeChange={(mode) => updateSource({ mode })}
                />
                <CopySideControls
                  side={COPY_TOOL_DESTINATION_LABEL}
                  year={resolvedDestination.year}
                  onYearChange={(year) => {
                    // Saved ticks are for the old year's documents.
                    setSavedDestination(null);
                    updateDestination({ year, expandedKeys: null });
                  }}
                  mode={resolvedDestination.mode}
                  onModeChange={(mode) => updateDestination({ mode })}
                />
              </div>
              <div className="flex w-full flex-row gap-2">
                <CopyModeControl
                  value={copyMode}
                  onChange={handleCopyModeChange}
                  destinationModeLabel={MODE_LABELS[resolvedDestination.mode]}
                  isPartialCopy={!isFullCopy}
                  note={
                    hasSwitchedToAddTo && copyMode === CopyMode.AddTo
                      ? COPY_TOOL_SWITCHED_TO_ADD_TO_NOTE
                      : undefined
                  }
                />
                <CopyOptionsControl
                  value={copyOptions}
                  onChange={setCopyOptions}
                />
              </div>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="rounded-md border border-gray-medium p-2">
                <CopyDataFilterControls
                  filter={activeFilter}
                  context={filterContext}
                  isFullCopy={isFullCopy}
                  modeLabel={MODE_LABELS[resolvedSource.mode]}
                  onMatchRuleChange={handleMatchRuleChange}
                  onCategoriesChange={handleCategoriesChange}
                />
              </div>

              <div className="rounded-md border border-gray-medium p-2">
                {/* The final pick, next to the summary it feeds. */}
                <CopyTaskChecklist
                  filter={activeFilter}
                  context={filterContext}
                  isFullCopy={isFullCopy}
                  modeLabel={MODE_LABELS[resolvedSource.mode]}
                  onTaskCheck={handleTaskCheck}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3 md:min-h-[10rem] md:flex-1 md:flex-row md:items-stretch">
            <CopySelectionTree
              label={COPY_TOOL_SOURCE_LABEL}
              hint={COPY_TOOL_SOURCE_HINT}
              treeData={sourceView.treeData}
              checkedKeys={resolvedSource.keys}
              expandedKeys={sourceView.expandedKeys}
              isLoading={isLoading}
              isError={isError}
              onCheckedKeysChange={(keys) =>
                // Freezes expansion, so unticking can't collapse a paddock.
                updateSource({
                  keys,
                  expandedKeys: resolvedSource.expandedKeys,
                })
              }
              onExpandedKeysChange={sourceView.onExpandedKeysChange}
              scrollToKey={hasEditedSource ? null : resolvedSource.keys[0]}
              searchQuery={sourceView.query}
              onSearchQueryChange={sourceView.setQuery}
              isSearching={sourceView.isSearching}
              groupBy={sourceView.groupBy}
              checkContext={sourceView.checkContext}
              onGroupByChange={sourceView.setGroupBy}
            />
            <SwapButton
              onSwap={handleSwap}
              disabled={
                isCopying || isLoading || isError || destinationPaddocksOnly
              }
              disabledReason={
                destinationPaddocksOnly
                  ? COPY_TOOL_SWAP_DISABLED_ADD_AS_NEW
                  : undefined
              }
            />
            <CopySelectionTree
              label={COPY_TOOL_DESTINATION_LABEL}
              hint={COPY_TOOL_DESTINATION_HINT(copyMode)}
              treeData={destinationView.treeData}
              checkedKeys={resolvedDestination.keys}
              expandedKeys={destinationView.expandedKeys}
              isLoading={isLoading}
              isError={isError}
              onCheckedKeysChange={(keys) =>
                updateDestination({
                  keys,
                  expandedKeys: resolvedDestination.expandedKeys,
                })
              }
              onExpandedKeysChange={destinationView.onExpandedKeysChange}
              searchQuery={destinationView.query}
              onSearchQueryChange={destinationView.setQuery}
              isSearching={destinationView.isSearching}
              groupBy={destinationView.groupBy}
              checkContext={destinationView.checkContext}
              onGroupByChange={destinationView.setGroupBy}
            />
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default CopyToolModal;
